import { Observable, of, throwError, OperatorFunction, timer } from 'rxjs';
import {
	map,
	filter,
	catchError,
	mergeMap,
	tap,
	debounceTime,
	distinctUntilChanged,
	switchMap,
	timeInterval,
	delayWhen,
	skipWhile,
	take,
} from 'rxjs/operators';
import { ParamMap } from '@angular/router';
import { errorTypes } from '../constants';
import {
	isString,
	isIdentifiableObject,
	idComparer,
} from './utility-functions';
import { UserPreferencesService } from '../services/user-preferences.service';
import { ListArgs } from '../api';
import { MatSnackBar } from '@angular/material/snack-bar';

export function grabParam(key: string) {
	return (source: Observable<ParamMap>) =>
		new Observable<string>((subscriber) => {
			return source
				.pipe(
					filter((params) => params.has(key)),
					map((params) => params.get(key))
				)
				.subscribe({
					next: (v) => subscriber.next(v),
					error: (e) => subscriber.error(e),
					complete: () => subscriber.complete(),
				});
		});
}

export function convertToInt() {
	return (source: Observable<string>) =>
		new Observable<number>((subscriber) => {
			return source
				.pipe(
					map((s) => parseInt(s, 10)),
					throwIf(isNaN, 'Not a valid ID.')
				)
				.subscribe({
					next: (v) => subscriber.next(v),
					error: (e) => subscriber.error(e),
					complete: () => subscriber.complete(),
				});
		});
}

export function throwIf<T>(project: (val: T) => boolean, message: string) {
	return (source: Observable<T>) =>
		new Observable<T>((subscriber) => {
			return source
				.pipe(
					mergeMap((v) =>
						project(v)
							? throwError({
									status: errorTypes.FRIENDLY_MESSAGE_CODE,
									response: message,
							  })
							: source
					)
				)
				.subscribe({
					next: (v) => subscriber.next(v),
					error: (e) => subscriber.error(e),
					complete: () => subscriber.complete(),
				});
		});
}

export function grabIdFromParams() {
	return (source: Observable<ParamMap>) =>
		new Observable<number>((subscriber) => {
			return source.pipe(grabParam('id'), convertToInt()).subscribe({
				next: (v) => subscriber.next(v),
				error: (e) => subscriber.error(e),
				complete: () => subscriber.complete(),
			});
		});
}

export function destructureToFitForm<T, R>(project: (val: T) => R) {
	return (source: Observable<T>) =>
		new Observable<R>((subscriber) => {
			return source.pipe(map(project)).subscribe({
				next: (v) => subscriber.next(v),
				error: (e) => subscriber.error(e),
				complete: () => subscriber.complete(),
			});
		});
}

export function errorSideEffects<T>(doThis: () => void) {
	return (source: Observable<T>) =>
		new Observable<T>((subscriber) => {
			return source
				.pipe(
					catchError((error) => {
						doThis();
						return of(error);
					})
				)
				.subscribe({
					next: (v) => subscriber.next(v),
					error: (e) => subscriber.error(e),
					complete: () => subscriber.complete(),
				});
		});
}

export function log() {
	return (source: Observable<any>) =>
		new Observable<any>((subscriber) => {
			// tslint:disable-next-line: no-console
			return source.subscribe({
				next: (v) => subscriber.next(v),
				error: (e) => subscriber.error(e),
				complete: () => subscriber.complete(),
			});
		});
}

export function ensureValidTypeAheadInput() {
	return (source: Observable<string | object>) =>
		new Observable<string>((subscriber) => {
			return source
				.pipe(
					filter<{ id?: number } | string, string>(isString),
					map((s) => s.trim()),
					filter((s) => !/[^a-zA-Z0-9\- ]/.test(s)),
					debounceTime(250),
					distinctUntilChanged()
				)
				.subscribe({
					next: (v) => subscriber.next(v),
					error: (e) => subscriber.error(e),
					complete: () => subscriber.complete(),
				});
		});
}

export function grabValidIdFromObject() {
	return (source: Observable<string | object>) =>
		new Observable<number>((subscriber) => {
			return source
				.pipe(
					filter<{ id?: number } | string, { id?: number }>(
						isIdentifiableObject
					),
					distinctUntilChanged(idComparer),
					map((obj) => obj.id),
					filter((id) => id > 0)
				)
				.subscribe({
					next: (v) => subscriber.next(v),
					error: (e) => subscriber.error(e),
					complete: () => subscriber.complete(),
				});
		});
}

export function addPagingListArgs(userPreferences: UserPreferencesService) {
	return (source: Observable<ListArgs>) =>
		new Observable<ListArgs>((subscriber) => {
			return source
				.pipe(
					switchMap((args) =>
						userPreferences.pageSize$.pipe(
							take(1),
							map((pageSize) => ({
								...(args ? args : {}),
								pageSize,
								pageNumber: 0,
							}))
						)
					)
				)
				.subscribe({
					next: (v) => subscriber.next(v),
					error: (e) => subscriber.error(e),
					complete: () => subscriber.complete(),
				});
		});
}

export function distinctUntilKeysChanged(...keys: string[]) {
	return (source: Observable<any>) =>
		new Observable<any>((subscriber) => {
			return source
				.pipe(
					distinctUntilChanged(
						(current, previous) =>
							!keys
								.map((k) => current[k] === previous[k])
								.includes(false)
					)
				)
				.subscribe({
					next: (v) => subscriber.next(v),
					error: (e) => subscriber.error(e),
					complete: () => subscriber.complete(),
				});
		});
}

export function preventFlicker(displayMs: number) {
	return (source: Observable<any>) =>
		new Observable<any>((subscriber) => {
			return source
				.pipe(
					skipWhile((value) => !value),
					timeInterval(),
					delayWhen(({ value, interval }) =>
						value
							? timer(0)
							: timer(Math.max(displayMs - interval, 0))
					),
					map((interval) => interval.value)
				)
				.subscribe({
					next: (v) => subscriber.next(v),
					error: (e) => subscriber.error(e),
					complete: () => subscriber.complete(),
				});
		});
}

export function notifyAfterSaving(snackbar: MatSnackBar) {
	return (source: Observable<any>) =>
		new Observable<any>((subscriber) => {
			return source
				.pipe(
					tap(() => {
						snackbar.open('Changes saved', '', {
							duration: 1000,
						});
					})
				)
				.subscribe({
					next: (v) => subscriber.next(v),
					error: (e) => subscriber.error(e),
					complete: () => subscriber.complete(),
				});
		});
}

// This is 'pluck' but with null-safety. Code is taken right from github and modified.
export function safelyPluck<T, R>(
	...properties: string[]
): OperatorFunction<T, R> {
	const length = properties.length;
	if (length === 0) {
		throw new Error('list of properties cannot be empty.');
	}
	return (source: Observable<T>) =>
		map(plucker(properties, length))(source as any);
}

function plucker(props: string[], length: number): (x: string) => any {
	const mapper = (x: any) => {
		let currentProp = x;
		for (let i = 0; i < length; i++) {
			const p = currentProp[props[i]];
			if (p === null) {
				return null;
			} else if (typeof p !== 'undefined') {
				currentProp = p;
			} else {
				return undefined;
			}
		}
		return currentProp;
	};

	return mapper;
}
